import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ZoomableImage } from "./ZoomableImage";

// High-performance product image carousel. Uses native CSS scroll-snap so
// swipe on mobile is buttery-smooth without JS. Arrow buttons + dot
// indicators are optional overlays for desktop / clarity. Each slide reuses
// <ZoomableImage/> so tap-to-zoom / pan still works.
//
// Props:
//   images: string[]                — deduped list of image URLs (2..N)
//   activeIndex?: number            — controlled active slide (optional)
//   onIndexChange?: (i:number)=>void — called when the visible slide changes
//   alt: string                     — alt/title base
export const ProductImageCarousel = ({
  images,
  activeIndex,
  onIndexChange,
  alt = "",
}) => {
  const railRef = useRef(null);
  const [current, setCurrent] = useState(activeIndex ?? 0);

  // Scroll to a given index (used by arrow buttons + external control).
  const scrollTo = useCallback(
    (idx, opts = { behavior: "smooth" }) => {
      const rail = railRef.current;
      if (!rail) return;
      const slide = rail.children[idx];
      if (!slide) return;
      rail.scrollTo({ left: slide.offsetLeft, behavior: opts.behavior });
    },
    [],
  );

  // Sync external activeIndex → scroll.
  useEffect(() => {
    if (typeof activeIndex === "number" && activeIndex !== current) {
      setCurrent(activeIndex);
      scrollTo(activeIndex, { behavior: "auto" });
    }
  }, [activeIndex, current, scrollTo]);

  // Track the current slide via IntersectionObserver for perfect accuracy on
  // both native swipe and programmatic scroll.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number(entry.target.dataset.idx);
            if (!Number.isNaN(idx) && idx !== current) {
              setCurrent(idx);
              onIndexChange?.(idx);
            }
          }
        });
      },
      { root: rail, threshold: [0.6] },
    );
    Array.from(rail.children).forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [images.length, current, onIndexChange]);

  if (!images || images.length === 0) return null;

  const go = (delta) => {
    const next = Math.min(images.length - 1, Math.max(0, current + delta));
    if (next !== current) scrollTo(next);
  };

  return (
    <div className="relative w-full h-full" data-testid="product-image-carousel">
      <div
        ref={railRef}
        data-testid="product-image-carousel-rail"
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {images.map((url, idx) => (
          <div
            key={`${url}-${idx}`}
            data-idx={idx}
            data-testid={`carousel-slide-${idx}`}
            className="relative w-full h-full shrink-0 snap-start snap-always"
          >
            <ZoomableImage src={url} alt={`${alt} – ${idx + 1}`} />
          </div>
        ))}
      </div>

      {images.length > 1 && (
        <>
          {/* Prev/next arrows — desktop-friendly; hidden edges on mobile. */}
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous image"
            data-testid="carousel-prev"
            disabled={current === 0}
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm border border-black/10 shadow items-center justify-center text-[#0B0C10] hover:bg-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next image"
            data-testid="carousel-next"
            disabled={current === images.length - 1}
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm border border-black/10 shadow items-center justify-center text-[#0B0C10] hover:bg-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>

          {/* Dot indicators. */}
          <div
            data-testid="carousel-dots"
            className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/25 backdrop-blur-sm rounded-full px-2 py-1"
          >
            {images.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => scrollTo(idx)}
                aria-label={`Go to image ${idx + 1}`}
                data-testid={`carousel-dot-${idx}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  idx === current
                    ? "bg-white w-4"
                    : "bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>

          {/* Slide counter for accessibility / clarity. */}
          <div
            data-testid="carousel-counter"
            className="absolute top-2 right-2 text-[10px] font-medium bg-black/40 text-white rounded-full px-2 py-0.5 backdrop-blur-sm"
          >
            {current + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};
